import { createContext, useContext } from "react";
import type { ReferenceOption } from "../api/catalogue-models";

export interface DestinationContextValue {
  destinationId: string;
  label: string;
  setDestination: (option: ReferenceOption | undefined) => void;
  href: (path: string) => string;
}

export const DestinationContext = createContext<DestinationContextValue | undefined>(undefined);
export const useDestinationContext = () => useContext(DestinationContext);

const scopedPaths = new Set([
  "/dashboard",
  "/partners",
  "/experiences",
  "/schedules",
  "/partner-setups/new",
]);
export function validDestinationId(value: unknown): string {
  return typeof value === "string" && value.length <= 64 && !/[\s/\\?#]/.test(value) ? value : "";
}
export function destinationHref(path: string, destinationId: string) {
  if (!validDestinationId(destinationId) || !/^\/(?!\/)/.test(path) || /[\\\r\n]/.test(path))
    return path;
  const url = new URL(path, "https://cyra.invalid");
  if (!scopedPaths.has(url.pathname) || url.searchParams.has("destinationId")) return path;
  url.searchParams.set("destinationId", destinationId);
  return url.pathname + url.search + url.hash;
}

export function destinationFromLocation(pathname: string, search: string, returnTo?: unknown) {
  const direct = /^\/destinations\/([^/]+)$/.exec(pathname)?.[1];
  const current = new URLSearchParams(search).get("destinationId");
  const parent =
    typeof returnTo === "string" && /^\/(?!\/)/.test(returnTo) && !/[\\\r\n]/.test(returnTo)
      ? new URL(returnTo, "https://cyra.invalid").searchParams.get("destinationId")
      : null;
  if (direct) {
    try {
      return validDestinationId(decodeURIComponent(direct));
    } catch {
      return "";
    }
  }
  const value = current ?? parent;
  // Missing context can inherit the workspace; an explicit empty value means All Destinations.
  return value === null ? undefined : validDestinationId(value);
}

export function replaceDestinationContext(pathname: string, search: string, destinationId: string) {
  const params = new URLSearchParams(search);
  for (const key of [
    "partnerId",
    "offeringId",
    "experienceId",
    "scheduleId",
    "page",
    "destinationPage",
    "search",
  ])
    params.delete(key);
  params.set("destinationId", validDestinationId(destinationId));
  const path =
    pathname === "/destinations" || /^\/destinations\//.test(pathname) ? "/experiences" : pathname;
  return `${path}${params.size ? `?${params}` : ""}`;
}
